import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Sparkles, Heart, Sun, BookOpen, Plus, Trash2, Edit2, Check, X, History, ChevronRight } from 'lucide-react';
import { DmoProgressCard } from '../components/DmoProgressCard';
import { TodayFollowUpsCard } from '../components/TodayFollowUpsCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Debounce hook for journal auto-save
function useDebounce(callback, delay) {
  const timeoutRef = useCallback(() => {
    let timeoutId = null;
    return (value) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => callback(value), delay);
    };
  }, [callback, delay]);
  
  return timeoutRef();
}

export const HomeTab = () => {
  const navigate = useNavigate();
  const { getAuthHeaders, user } = useAuth();
  const [powerStatement, setPowerStatement] = useState('');
  const [editingPower, setEditingPower] = useState(false);
  const [tempPower, setTempPower] = useState('');
  
  const [gratitudeItems, setGratitudeItems] = useState([]);
  const [newGratitude, setNewGratitude] = useState('');
  const [gratitudeArchive, setGratitudeArchive] = useState([]);
  const [showGratitudeArchive, setShowGratitudeArchive] = useState(false);
  
  const [mantra, setMantra] = useState('');
  const [editingMantra, setEditingMantra] = useState(false);
  const [tempMantra, setTempMantra] = useState('');
  
  const [journal, setJournal] = useState('');
  const [journalArchive, setJournalArchive] = useState([]);
  const [showJournalArchive, setShowJournalArchive] = useState(false);

  const [dmoStats, setDmoStats] = useState({ completed: 0, total: 0 });
  const [streak, setStreak] = useState({ current_streak: 0, longest_streak: 0, completed_today: false });

  const fetchHomeData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [powerRes, gratRes, mantraRes, journalRes] = await Promise.all([
        axios.get(`${API}/home/power-statement`, headers),
        axios.get(`${API}/home/gratitude`, headers),
        axios.get(`${API}/home/mantra`, headers),
        axios.get(`${API}/home/journal`, headers),
      ]);
      
      setPowerStatement(powerRes.data.content || '');
      setGratitudeItems(gratRes.data.items || []);
      setMantra(mantraRes.data.content || '');
      setJournal(journalRes.data.content || '');
    } catch (error) {
      console.error('Error fetching home data:', error);
    }
  }, [getAuthHeaders]);

  const fetchDmoStats = useCallback(async () => {
    try {
      const [tasksRes, streakRes] = await Promise.all([
        axios.get(`${API}/tasks?task_type=recurring`, getAuthHeaders()),
        axios.get(`${API}/tasks/dmo-streak`, getAuthHeaders()),
      ]);
      const tasks = tasksRes.data || [];
      const completed = tasks.filter(t => t.completed).length;
      setDmoStats({ completed, total: tasks.length });
      setStreak(streakRes.data);
    } catch (error) {
      console.error('Error fetching DMO stats:', error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchHomeData();
    fetchDmoStats();
  }, [fetchHomeData, fetchDmoStats]);

  const updatePowerStatement = async () => {
    try {
      await axios.put(`${API}/home/power-statement`, { content: tempPower }, getAuthHeaders());
      setPowerStatement(tempPower);
      setEditingPower(false);
      toast.success('Power statement updated!');
    } catch (error) {
      toast.error('Failed to update power statement');
    }
  };

  const addGratitude = async () => {
    if (!newGratitude.trim()) return;
    try {
      const response = await axios.post(`${API}/home/gratitude`, { content: newGratitude }, getAuthHeaders());
      setGratitudeItems(response.data.items || []);
      setNewGratitude('');
      toast.success('Gratitude added!');
    } catch (error) {
      toast.error('Failed to add gratitude');
    }
  };

  const deleteGratitude = async (itemId) => {
    try {
      await axios.delete(`${API}/home/gratitude/${itemId}`, getAuthHeaders());
      setGratitudeItems(items => items.filter(i => i.item_id !== itemId));
      toast.success('Item removed');
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const fetchGratitudeArchive = async () => {
    try {
      const response = await axios.get(`${API}/home/gratitude/archive`, getAuthHeaders());
      setGratitudeArchive(response.data);
      setShowGratitudeArchive(true);
    } catch (error) {
      toast.error('Failed to load archive');
    }
  };

  const updateMantra = async () => {
    try {
      await axios.put(`${API}/home/mantra`, { content: tempMantra }, getAuthHeaders());
      setMantra(tempMantra);
      setEditingMantra(false);
      toast.success('Mantra updated!');
    } catch (error) {
      toast.error('Failed to update mantra');
    }
  };

  // Debounced journal save to prevent glitching while typing
  const saveJournalToServer = async (content) => {
    try {
      await axios.put(`${API}/home/journal`, { content }, getAuthHeaders());
    } catch (error) {
      console.error('Failed to auto-save journal');
    }
  };

  const [debouncedSaveJournal] = useState(() => {
    let timeoutId = null;
    return (content) => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => saveJournalToServer(content), 1000);
    };
  });

  const handleJournalChange = (e) => {
    const content = e.target.value;
    setJournal(content);
    debouncedSaveJournal(content);
  };

  const updateJournal = async (content) => {
    try {
      await axios.put(`${API}/home/journal`, { content }, getAuthHeaders());
      setJournal(content);
    } catch (error) {
      console.error('Failed to auto-save journal');
    }
  };

  const fetchJournalArchive = async () => {
    try {
      const response = await axios.get(`${API}/home/journal/archive`, getAuthHeaders());
      setJournalArchive(response.data);
      setShowJournalArchive(true);
    } catch (error) {
      toast.error('Failed to load archive');
    }
  };

  return (
    <div className="p-4 space-y-4 max-w-2xl mx-auto" data-testid="home-tab">
      {/* Header */}
      <div className="text-center py-4">
        <h1 className="text-2xl font-outfit font-bold text-violet-600 dark:text-violet-400">
          Welcome{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
        </p>
      </div>

      <TodayFollowUpsCard />

      {/* Power Statement */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
        <Card className="bg-gradient-to-br from-violet-100 to-violet-50 dark:from-violet-900/30 dark:to-violet-800/20 border-violet-200 dark:border-violet-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-violet-700 dark:text-violet-300">
              <Sparkles className="w-5 h-5" />
              Daily Power Statement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editingPower ? (
              <div className="space-y-2">
                <Textarea
                  value={tempPower}
                  onChange={(e) => setTempPower(e.target.value)}
                  placeholder="Write your power statement..."
                  className="min-h-[80px] bg-white dark:bg-slate-800"
                  data-testid="power-statement-input"
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setEditingPower(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={updatePowerStatement} className="bg-violet-600 hover:bg-violet-700" data-testid="save-power-btn">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start justify-between gap-2">
                <p className="text-lg font-medium text-violet-800 dark:text-violet-200 italic">
                  &ldquo;{powerStatement || 'Click edit to add your power statement'}&rdquo;
                </p>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  onClick={() => { setTempPower(powerStatement); setEditingPower(true); }}
                  data-testid="edit-power-btn"
                >
                  <Edit2 className="w-4 h-4 text-violet-600" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Gratitude Box */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
        <Card className="bg-gradient-to-br from-pink-100 to-pink-50 dark:from-pink-900/30 dark:to-pink-800/20 border-pink-200 dark:border-pink-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-pink-700 dark:text-pink-300">
                <Heart className="w-5 h-5" />
                Morning Gratitude
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchGratitudeArchive}
                className="text-pink-600 hover:text-pink-700"
                data-testid="gratitude-archive-btn"
              >
                <History className="w-4 h-4 mr-1" />
                Archive
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                value={newGratitude}
                onChange={(e) => setNewGratitude(e.target.value)}
                placeholder="I am grateful for..."
                onKeyDown={(e) => e.key === 'Enter' && addGratitude()}
                className="bg-white dark:bg-slate-800"
                data-testid="gratitude-input"
              />
              <Button onClick={addGratitude} className="bg-pink-600 hover:bg-pink-700" data-testid="add-gratitude-btn">
                <Plus className="w-4 h-4" />
              </Button>
            </div>
            {gratitudeItems.length > 0 && (
              <p className="text-xs text-pink-500 dark:text-pink-400 text-center">
                {gratitudeItems.length} item{gratitudeItems.length !== 1 ? 's' : ''} • Scroll to see all
              </p>
            )}
            <ScrollArea className="h-[250px] pr-2">
              <ul className="space-y-2">
                {gratitudeItems.length === 0 ? (
                  <p className="text-center text-pink-400 dark:text-pink-500 py-4 text-sm">
                    Start your day with gratitude...
                  </p>
                ) : (
                  gratitudeItems.map((item, index) => (
                    <motion.li
                      key={item.item_id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-center justify-between bg-white/50 dark:bg-slate-800/50 p-2 rounded-lg"
                    >
                      <span className="text-pink-800 dark:text-pink-200">{item.content}</span>
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => deleteGratitude(item.item_id)}
                        className="h-8 w-8 text-pink-600 hover:text-pink-700 hover:bg-pink-100 flex-shrink-0"
                        data-testid={`delete-gratitude-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </motion.li>
                  ))
                )}
              </ul>
            </ScrollArea>
          </CardContent>
        </Card>
      </motion.div>

      {/* Mantra */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
        <Card className="bg-gradient-to-br from-amber-100 to-amber-50 dark:from-amber-900/30 dark:to-amber-800/20 border-amber-200 dark:border-amber-800">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
              <Sun className="w-5 h-5" />
              Daily Mantra / Affirmation
            </CardTitle>
          </CardHeader>
          <CardContent>
            {editingMantra ? (
              <div className="space-y-2">
                <Textarea
                  value={tempMantra}
                  onChange={(e) => {
                    setTempMantra(e.target.value);
                    // Auto-resize textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                  placeholder="Write your daily mantra or affirmation... You can make this as long as you want!"
                  className="min-h-[100px] bg-white dark:bg-slate-800 resize-none overflow-hidden"
                  style={{ height: 'auto' }}
                  data-testid="mantra-input"
                  onFocus={(e) => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                  }}
                />
                <div className="flex gap-2 justify-end">
                  <Button size="sm" variant="ghost" onClick={() => setEditingMantra(false)}>
                    <X className="w-4 h-4" />
                  </Button>
                  <Button size="sm" onClick={updateMantra} className="bg-amber-600 hover:bg-amber-700" data-testid="save-mantra-btn">
                    <Check className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ) : (
              <div 
                className="flex items-start justify-between gap-2 cursor-pointer group"
                onClick={() => { setTempMantra(mantra); setEditingMantra(true); }}
              >
                <p className="text-amber-800 dark:text-amber-200 font-medium whitespace-pre-wrap flex-1">
                  {mantra || 'Tap here to add your daily mantra or affirmation...'}
                </p>
                <Button 
                  size="icon" 
                  variant="ghost" 
                  className="opacity-60 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  onClick={(e) => { 
                    e.stopPropagation();
                    setTempMantra(mantra); 
                    setEditingMantra(true); 
                  }}
                  data-testid="edit-mantra-btn"
                >
                  <Edit2 className="w-4 h-4 text-amber-600" />
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </motion.div>

      {/* Journal */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
        <Card className="bg-gradient-to-br from-emerald-100 to-emerald-50 dark:from-emerald-900/30 dark:to-emerald-800/20 border-emerald-200 dark:border-emerald-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-emerald-700 dark:text-emerald-300">
                <BookOpen className="w-5 h-5" />
                Journal Entry
              </CardTitle>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={fetchJournalArchive}
                className="text-emerald-600 hover:text-emerald-700"
                data-testid="journal-archive-btn"
              >
                <History className="w-4 h-4 mr-1" />
                Archive
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={journal}
              onChange={handleJournalChange}
              placeholder="Write your thoughts, reflections, and ideas..."
              className="min-h-[150px] bg-white dark:bg-slate-800 resize-none"
              data-testid="journal-textarea"
            />
            <p className="text-xs text-muted-foreground mt-2 text-right">Auto-saved</p>
          </CardContent>
        </Card>
      </motion.div>

      {/* DMO Tasks Button with Progress + Streak */}
      <DmoProgressCard
        dmoStats={dmoStats}
        streak={streak}
        onOpen={() => navigate('/tasks')}
      />

      {/* Gratitude Archive Dialog */}
      <Dialog open={showGratitudeArchive} onOpenChange={setShowGratitudeArchive}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Heart className="w-5 h-5 text-pink-600" />
              Gratitude Archive
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {gratitudeArchive.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No archived entries yet</p>
            ) : (
              <div className="space-y-4">
                {gratitudeArchive.map((entry) => (
                  <div key={entry.entry_id} className="border-b pb-3">
                    <p className="font-medium text-pink-600 dark:text-pink-400 mb-2">
                      {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <ul className="space-y-1">
                      {entry.items?.map((item, idx) => (
                        <li key={idx} className="text-sm flex items-start gap-2">
                          <ChevronRight className="w-4 h-4 text-pink-400 mt-0.5 flex-shrink-0" />
                          {item.content}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Journal Archive Dialog */}
      <Dialog open={showJournalArchive} onOpenChange={setShowJournalArchive}>
        <DialogContent className="max-w-md max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-emerald-600" />
              Journal Archive
            </DialogTitle>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            {journalArchive.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No archived entries yet</p>
            ) : (
              <div className="space-y-4">
                {journalArchive.map((entry) => (
                  <div key={entry.journal_id} className="border-b pb-3">
                    <p className="font-medium text-emerald-600 dark:text-emerald-400 mb-2">
                      {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                    </p>
                    <p className="text-sm whitespace-pre-wrap">{entry.content || 'No entry'}</p>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HomeTab;
